export class CoordinateTransformer {
    #videoElement;
    #canvas;

    constructor(videoElement, canvas) {
        this.#videoElement = videoElement;
        this.#canvas = canvas;
    }

    getRotationAngle() {
        let angle = 0;
        const style = this.#videoElement.getAttribute('style');
        if (style) {
            const angleMatch = style.match(/rotate\((-?\d+)deg\)/);
            if (angleMatch && angleMatch[1]) {
                angle = parseInt(angleMatch[1], 10);
            }
        }
        return angle;
    }

    getVideoInfo() {
        const rotationAngle = this.getRotationAngle();
        const videoElementDimensions = this.#videoElement.getBoundingClientRect();
        let { width, height } = videoElementDimensions;
        let streamWidth = width;
        let streamHeight = height;

        if (rotationAngle === 90 || rotationAngle === 270) {
            streamWidth = height;
            streamHeight = width;
        }

        return { rotationAngle, videoElementDimensions, streamWidth, streamHeight };
    }

    calculateAllowedDrawingArea() {
        const {
            left: videoElementX1,
            top: videoElementY1,
            right: videoElementX2,
            bottom: videoElementY2,
        } = this.#videoElement.getBoundingClientRect();

        const {
            left: canvasX1,
            top: canvasY1,
            right: canvasX2,
            bottom: canvasY2,
            width: canvasWidth,
            height: canvasHeight,
        } = this.#canvas.getBoundingClientRect();

        const x1 = videoElementX1 - canvasX1;
        const y1 = videoElementY1 - canvasY1;
        const x2 = canvasX2 - videoElementX2;
        const y2 = canvasY2 - videoElementY2;

        const minX = x1 > 0 ? x1 : 0;
        const minY = y1 > 0 ? y1 : 0;
        const maxX = x2 > 0 ? canvasWidth - x2 : canvasWidth;
        const maxY = y2 > 0 ? canvasHeight - y2 : canvasHeight;

        return { minX, minY, maxX, maxY };
    }

    isPointAllowed(x, y, allowedArea) {
        const { minX, minY, maxX, maxY } = allowedArea;
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
    }

    clampToAllowedArea(x, y, allowedArea) {
        const { minX, minY, maxX, maxY } = allowedArea;
        return {
            x: Math.max(minX, Math.min(maxX, x)),
            y: Math.max(minY, Math.min(maxY, y)),
        };
    }

    #getCanvasBaseRelativeToStream(rotationAngle, videoElementDimensions) {
        const { left: videoElementX1, top: videoElementY1, right: videoElementX2, bottom: videoElementY2 } = videoElementDimensions;
        const { left: canvasX1, top: canvasY1 } = this.#canvas.getBoundingClientRect();

        const rotationMap = {
            0: { x: videoElementX1 - canvasX1, y: videoElementY1 - canvasY1 },
            90: { x: videoElementX2 - canvasX1, y: videoElementY1 - canvasY1 },
            180: { x: videoElementX2 - canvasX1, y: videoElementY2 - canvasY1 },
            270: { x: videoElementX1 - canvasX1, y: videoElementY2 - canvasY1 },
        };

        return rotationMap[rotationAngle] || rotationMap[0];
    }

    toRecenterCoords(startPoint) {
        const { rotationAngle, videoElementDimensions, streamWidth, streamHeight } = this.getVideoInfo();
        const base = this.#getCanvasBaseRelativeToStream(rotationAngle, videoElementDimensions);

        const x1 = Math.abs(startPoint.x - base.x);
        const y1 = Math.abs(startPoint.y - base.y);

        const isRotated90Or270 = rotationAngle === 90 || rotationAngle === 270;
        return {
            centerX: isRotated90Or270 ? y1 : x1,
            centerY: isRotated90Or270 ? x1 : y1,
            streamWidth,
            streamHeight,
        };
    }

    toRegionCoords(startPoint, endPoint) {
        const { rotationAngle, videoElementDimensions, streamWidth, streamHeight } = this.getVideoInfo();

        // Adjust direction based on rotation
        const adjusted = this.#adjustDirectionForRotation(startPoint, endPoint, rotationAngle);
        const base = this.#getCanvasBaseRelativeToStream(rotationAngle, videoElementDimensions);

        let x1 = Math.abs(adjusted.start.x - base.x);
        let y1 = Math.abs(adjusted.start.y - base.y);
        let x2 = Math.abs(adjusted.end.x - base.x);
        let y2 = Math.abs(adjusted.end.y - base.y);

        const isRotated90Or270 = rotationAngle === 90 || rotationAngle === 270;
        let coords;

        if (isRotated90Or270) {
            coords = { x1: y1, y1: x1, x2: y2, y2: x2 };
        } else {
            coords = { x1, y1, x2, y2 };
        }

        // Force drag direction to be top-to-bottom
        coords = this.#forceDragDirectionDown(coords);

        return { ...coords, streamWidth, streamHeight };
    }

    #adjustDirectionForRotation(startPoint, endPoint, rotationAngle) {
        let inverseDirection = false;

        switch (rotationAngle) {
            case 180:
                inverseDirection = true;
                break;
            case 90:
                if ((startPoint.x > endPoint.x && endPoint.y > startPoint.y) || (startPoint.x < endPoint.x && endPoint.y < startPoint.y)) {
                    inverseDirection = true;
                }
                break;
            case 270:
                if ((startPoint.x < endPoint.x && endPoint.y > startPoint.y) || (startPoint.x > endPoint.x && endPoint.y < startPoint.y)) {
                    inverseDirection = true;
                }
                break;
        }

        if (inverseDirection) {
            return { start: endPoint, end: startPoint };
        }
        return { start: startPoint, end: endPoint };
    }

    #forceDragDirectionDown(coords) {
        if (coords.y1 > coords.y2) {
            return { ...coords, y1: coords.y2, y2: coords.y1 };
        }
        return coords;
    }
}
